import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { toast } from '@/ui/toast';
import { showSelectionLimitReachedToast } from '@/ui/selection-limit-toast';
import { Sort } from '@/types/sort.type';
import { useMarketplaceApi } from '@/context/marketplace.context';

type UseSelectionReviewsActionParams = {
    accessToken: string;
    selectionSessionId: string;
    total: number;
    filtersQuery: string[];
    sort: Sort;
    maxSelectedItems: number | null;
    setPickedReviews?: (val: string[]) => void;
    waitForReviewSelectionReconcile: () => Promise<void>;
    enqueueReviewSelectionMutation: <T>(mutation: () => Promise<T>) => Promise<T>;
    getCurrentPickedReviews: () => string[];
    setIsAllPickLoading: Dispatch<SetStateAction<boolean>>;
};

type UseSelectionReviewsActionReturn = {
    onAllPickedReviews: () => Promise<void>;
};

const selectionReviewsLogger = createClientLogger({
    scope: 'selection-reviews',
});

export const useSelectionReviewsAction = ({
    accessToken,
    selectionSessionId,
    total,
    filtersQuery,
    sort,
    maxSelectedItems,
    setPickedReviews,
    waitForReviewSelectionReconcile,
    enqueueReviewSelectionMutation,
    getCurrentPickedReviews,
    setIsAllPickLoading,
}: UseSelectionReviewsActionParams): UseSelectionReviewsActionReturn => {
    const { reviewsApi } = useMarketplaceApi();

    const onAllPickedReviews = useCallback(async () => {
        if (!setPickedReviews || !selectionSessionId) {
            return;
        }

        await waitForReviewSelectionReconcile();
        const currentPickedReviews = getCurrentPickedReviews();

        if (currentPickedReviews.length || total === 0) {
            try {
                await enqueueReviewSelectionMutation(() =>
                    reviewsApi.clearReviewSelectionSession(selectionSessionId, {
                        authToken: accessToken,
                    }),
                );
                setPickedReviews([]);
            } catch (error) {
                logClientError({
                    logger: selectionReviewsLogger,
                    message: '[client.review-selection.session-clear] Failed to clear review selection session.',
                    error,
                    context: {
                        selectionSessionId,
                        total,
                    },
                });
                toast.error('Не удалось снять выделение');
            }

            return;
        }

        setIsAllPickLoading(true);

        try {
            const reviewIdsResponse = await enqueueReviewSelectionMutation(() =>
                reviewsApi.selectAllReviewSelectionSession(
                    {
                        sessionId: selectionSessionId,
                        search: filtersQuery,
                        ...sort,
                    },
                    { authToken: accessToken },
                ),
            );

            setPickedReviews(reviewIdsResponse.data.ids);

            if (maxSelectedItems !== null && reviewIdsResponse.data.ids.length < total) {
                showSelectionLimitReachedToast({ maxSelectedItems, scope: 'reviews' });
            }
        } catch (error) {
            logClientError({
                logger: selectionReviewsLogger,
                message: '[client.review-selection.select-all] Failed to select all reviews in selection session.',
                error,
                context: {
                    selectionSessionId,
                    total,
                    filtersQuery,
                    sort,
                },
            });
            toast.error('Не удалось выделить все отзывы');
        } finally {
            setIsAllPickLoading(false);
        }
    }, [
        accessToken,
        enqueueReviewSelectionMutation,
        filtersQuery,
        getCurrentPickedReviews,
        maxSelectedItems,
        selectionSessionId,
        setIsAllPickLoading,
        setPickedReviews,
        sort,
        total,
        waitForReviewSelectionReconcile,
    ]);

    return {
        onAllPickedReviews,
    };
};
